import React, { useState, useEffect, useMemo } from "react";
import LifeSaverLayout from "../../components/LifeSaverLayout";
import api, { baseURL } from "../../services/api";
import {
  FiVolume2,
  FiMessageCircle,
  FiSearch,
  FiFileText,
  FiDownload,
  FiClock,
} from "react-icons/fi";

const MyCommunications = () => {
  const [communications, setCommunications] = useState([]);
  const [loading, setLoading] = useState(true);

  // Search filters
  const [searchTitle, setSearchTitle] = useState("");
  const [searchMessage, setSearchMessage] = useState("");
  const [searchMonth, setSearchMonth] = useState("");
  const [searchYear, setSearchYear] = useState("");

  useEffect(() => {
    fetchCommunications();
  }, []);

  const fetchCommunications = async () => {
    try {
      const response = await api.get("/communications/my");
      setCommunications(response.data);
    } catch (error) {
      console.error("Error fetching communications:", error);
    } finally {
      setLoading(false);
    }
  };

  // Get unique years from communications for year filter dropdown
  const availableYears = useMemo(() => {
    const years = communications.map((comm) =>
      new Date(comm.message_date).getFullYear()
    );
    return [...new Set(years)].sort((a, b) => b - a);
  }, [communications]);

  // Filtered communications based on search criteria
  const filteredCommunications = useMemo(() => {
    return communications.filter((comm) => {
      const commDate = new Date(comm.message_date);
      const commMonth = commDate.getMonth() + 1;
      const commYear = commDate.getFullYear();

      // Title filter
      if (
        searchTitle &&
        !(comm.title || "").toLowerCase().includes(searchTitle.toLowerCase())
      ) {
        return false;
      }

      // Message filter
      if (
        searchMessage &&
        !(comm.message || "")
          .toLowerCase()
          .includes(searchMessage.toLowerCase())
      ) {
        return false;
      }

      // Month filter
      if (searchMonth && commMonth !== parseInt(searchMonth)) {
        return false;
      }

      // Year filter
      if (searchYear && commYear !== parseInt(searchYear)) {
        return false;
      }

      return true;
    });
  }, [communications, searchTitle, searchMessage, searchMonth, searchYear]);

  const clearFilters = () => {
    setSearchTitle("");
    setSearchMessage("");
    setSearchMonth("");
    setSearchYear("");
  };

  if (loading) {
    return (
      <LifeSaverLayout>
        <div className="spinner"></div>
      </LifeSaverLayout>
    );
  }

  return (
    <LifeSaverLayout>
      <div className="page-header">
        <h1 className="page-title">Communications</h1>
        <span className="comm-count-badge">
          {filteredCommunications.length} messages
        </span>
      </div>

      {/* Search Filters */}
      <div className="card" style={{ marginBottom: "1.5rem" }}>
        <div className="search-filters">
          <h3
            style={{
              marginBottom: "1rem",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            <FiSearch /> Search & Filter
          </h3>
          <div
            className="filter-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "1rem",
            }}
          >
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Title</label>
              <input
                type="text"
                placeholder="Search by title..."
                value={searchTitle}
                onChange={(e) => setSearchTitle(e.target.value)}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Message</label>
              <input
                type="text"
                placeholder="Search by message..."
                value={searchMessage}
                onChange={(e) => setSearchMessage(e.target.value)}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Month</label>
              <select
                value={searchMonth}
                onChange={(e) => setSearchMonth(e.target.value)}
              >
                <option value="">All Months</option>
                <option value="1">January</option>
                <option value="2">February</option>
                <option value="3">March</option>
                <option value="4">April</option>
                <option value="5">May</option>
                <option value="6">June</option>
                <option value="7">July</option>
                <option value="8">August</option>
                <option value="9">September</option>
                <option value="10">October</option>
                <option value="11">November</option>
                <option value="12">December</option>
              </select>
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Year</label>
              <select
                value={searchYear}
                onChange={(e) => setSearchYear(e.target.value)}
              >
                <option value="">All Years</option>
                {availableYears.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div
            style={{
              marginTop: "1rem",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span style={{ color: "#666", fontSize: "0.9rem" }}>
              Showing {filteredCommunications.length} of {communications.length}{" "}
              communications
            </span>
            {(searchTitle || searchMessage || searchMonth || searchYear) && (
              <button
                className="btn btn-secondary"
                onClick={clearFilters}
                style={{ padding: "0.5rem 1rem" }}
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>
      </div>

      {filteredCommunications.length > 0 ? (
        <div className="comm-timeline">
          {filteredCommunications.map((comm) => (
            <div key={comm.id} className="comm-timeline-item">
              <div className="comm-timeline-date">
                <div className="comm-date-box">
                  <span className="comm-date-day">
                    {new Date(comm.message_date).getDate()}
                  </span>
                  <span className="comm-date-month">
                    {new Date(comm.message_date).toLocaleDateString("en-US", {
                      month: "short",
                    })}
                  </span>
                  <span className="comm-date-year">
                    {new Date(comm.message_date).getFullYear()}
                  </span>
                </div>
              </div>
              <div className="comm-timeline-connector">
                <div className="comm-timeline-dot"></div>
                <div className="comm-timeline-line"></div>
              </div>
              <div className="comm-timeline-content">
                <div className="comm-card">
                  <div className="comm-card-header">
                    <FiMessageCircle className="comm-card-icon" />
                    <h3 className="comm-card-title">
                      {comm.title || "Announcement"}
                    </h3>
                    <span className="comm-card-timestamp">
                      <FiClock style={{ marginRight: "4px" }} />
                      {new Date(comm.created_at).toLocaleString("en-US", {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </span>
                  </div>
                  {comm.message && (
                    <div className="comm-card-body">
                      <p className="comm-card-message">{comm.message}</p>
                    </div>
                  )}
                  {comm.voice_file_path && (
                    <div className="comm-card-audio">
                      <div className="comm-audio-label">
                        <FiVolume2 />
                        <span>Voice Message</span>
                      </div>
                      <audio controls className="comm-audio-player">
                        <source src={`${baseURL}${comm.voice_file_path}`} />
                        Your browser does not support the audio element.
                      </audio>
                    </div>
                  )}
                  {comm.document_file_path && (
                    <div className="comm-card-document">
                      <div className="comm-document-label">
                        <FiFileText />
                        <span>Document</span>
                      </div>
                      <a
                        href={`${baseURL}${comm.document_file_path}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-secondary"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        <FiDownload />
                        View/Download Document
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card">
          <div className="comm-empty-state">
            <div className="comm-empty-icon">
              <FiMessageCircle />
            </div>
            <h3>
              {communications.length === 0
                ? "No Communications Yet"
                : "No Matching Communications"}
            </h3>
            <p>
              {communications.length === 0
                ? "You don't have any communications yet. Check back later!"
                : "No communications match your search criteria. Try adjusting your filters."}
            </p>
            {communications.length > 0 && (
              <button
                className="btn btn-primary"
                onClick={clearFilters}
                style={{ marginTop: "1rem" }}
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>
      )}
    </LifeSaverLayout>
  );
};

export default MyCommunications;
